import { Medal, Trophy, Award } from "lucide-react";
import { EVENT5K } from "@/constants/testIds";

const WINNERS_PHOTO = "/images/executive-winners.png";

const PODIUM = [
  { place: "2nd", icon: Medal, color: "#C0C0C0", bg: "from-[#3a3a3a] to-[#1a1a1a]", label: "Silver Medal", h: "h-40 md:h-48" },
  { place: "1st", icon: Trophy, color: "#FFD447", bg: "from-[#00A651] to-[#1FBF3E]", label: "Gold Medal", h: "h-52 md:h-64" },
  { place: "3rd", icon: Award, color: "#CD7F32", bg: "from-[#4a2a12] to-[#2a1a08]", label: "Bronze Medal", h: "h-36 md:h-40" },
];

export default function Awards() {
  return (
    <section id="awards" data-testid="section-awards" className="relative bg-[#0A0A0A] py-24 md:py-32 border-t border-[#27272A]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#EC008C] font-bold mb-4">Awards &amp; Recognition</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white">
            Run for the <span className="text-[#00A651]">medal</span>.
          </h2>
          <p className="mt-4 text-lg text-[#A3A3A3]">
            Top finishers take home a <span className="text-white font-semibold">custom medal</span> — 1st, 2nd, and 3rd place across the 5K. Bring your race face.
          </p>
        </div>

        {/* Podium with animated medals */}
        <div className="mt-14 grid grid-cols-3 gap-3 md:gap-6 items-end max-w-3xl mx-auto">
          {PODIUM.map(({ place, icon: Icon, color, bg, label, h }, i) => (
            <div key={place} className="flex flex-col items-center">
              <div className="anim-float mb-4" style={{ animationDelay: `${i * 0.2}s` }}>
                <div className="relative">
                  <span className="absolute inset-0 rounded-full anim-ring" style={{ borderColor: color, borderWidth: "2px", borderStyle: "solid" }} />
                  <div
                    className="relative w-20 h-20 md:w-28 md:h-28 rounded-full border-4 border-white/20 flex items-center justify-center shadow-2xl"
                    style={{ background: `radial-gradient(circle at 30% 30%, ${color}, #1a1a1a)` }}
                  >
                    <Icon className="w-8 h-8 md:w-12 md:h-12 text-black" strokeWidth={2.5} />
                  </div>
                  {/* Ribbon tails */}
                  <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
                    <span className="w-2 h-6 bg-[#EC008C] rotate-6" />
                    <span className="w-2 h-6 bg-[#00A651] -rotate-6" />
                  </div>
                </div>
              </div>
              <div
                className={`w-full ${h} bg-gradient-to-t ${bg} border-t-4 flex flex-col items-center justify-center relative`}
                style={{ borderColor: color }}
              >
                <div className="font-display text-4xl md:text-6xl font-black text-white leading-none">{place}</div>
                <div className="mt-1 text-[9px] md:text-xs uppercase tracking-[0.25em] font-bold" style={{ color }}>
                  Place
                </div>
                <div className="mt-2 text-[9px] md:text-[10px] uppercase tracking-widest text-white/60">{label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Last year's winners photo */}
        <div className="mt-20 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7 relative group overflow-hidden border border-[#27272A]">
            <img
              src={WINNERS_PHOTO}
              alt="Last year's 1st, 2nd, and 3rd place 5K winners with the Executive Director"
              loading="lazy"
              className="w-full aspect-[4/3] object-cover object-top transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            <div className="absolute top-3 left-3 bg-[#00A651] text-black text-[10px] uppercase tracking-[0.24em] font-black px-3 py-1 anim-bounce-slow">
              2025 Podium
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#00A651] font-bold mb-4">Last Year&apos;s Winners</div>
            <h3 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight text-white">
              With our <span className="text-[#EC008C]">Executive Director</span>.
            </h3>
            <p className="mt-4 text-base md:text-lg text-[#A3A3A3]">
              Bibs #227, #221, and #213 stood on the 2025 podium alongside our Executive Director. In 2026,
              your name could be here — every mile counts, every finish matters.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-2 border border-[#00A651]/50 bg-[#00A651]/10 text-[#00A651] px-3 py-1.5 text-xs uppercase tracking-widest font-bold">
                <Trophy className="w-3.5 h-3.5" /> 1st · #227
              </span>
              <span className="inline-flex items-center gap-2 border border-white/30 text-white px-3 py-1.5 text-xs uppercase tracking-widest font-bold">
                <Medal className="w-3.5 h-3.5" /> 2nd · #221
              </span>
              <span className="inline-flex items-center gap-2 border border-[#CD7F32]/60 text-[#CD7F32] px-3 py-1.5 text-xs uppercase tracking-widest font-bold">
                <Award className="w-3.5 h-3.5" /> 3rd · #213
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
